import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import { MaskLines } from "./Motion";
import { InkFilters } from "./InkFilters";

const Letters = () => (
  <span className="ink-breathe flex overflow-hidden">
    {"MINKA".split("").map((ch, i) => (
      <motion.span
        key={i}
        className="ink-damage-sm inline-block"
        initial={{ y: "105%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1], delay: 0.3 + i * 0.09 }}
      >
        {ch}
      </motion.span>
    ))}
  </span>
);

export const Hero = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 60, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 60, damping: 18 });
  const { scrollY } = useScroll();
  const titleY = useTransform(scrollY, [0, 700], [0, -160]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section onMouseMove={onMove} className="relative flex min-h-[100svh] items-end overflow-hidden px-5 pb-16 pt-32 md:px-10 md:pb-24" style={{ perspective: 1200 }} data-testid="hero-section">
      <InkFilters />
      <motion.div style={{ opacity: fade }} className="pointer-events-none absolute inset-x-0 top-[22%] z-10 flex justify-center px-6 md:top-[20%]">
        <motion.p
          data-testid="hero-wish-tagline"
          className="text-center font-mono-s text-xs uppercase leading-loose tracking-[0.3em] text-[#E2E0D8] drop-shadow-[0_2px_18px_rgba(0,0,0,0.9)] sm:text-base md:text-lg"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.5, delay: 0.5 }}
        >
          Introducing the next epic Asian thriller: think a Japanese version of Twilight, meets Ring, meets Rocky.
        </motion.p>
      </motion.div>
      <motion.div style={{ opacity: fade }} className="pointer-events-none absolute right-5 top-28 hidden font-display md:block text-5xl leading-none text-[#C81D25]/80 [writing-mode:vertical-rl] md:right-12 md:text-7xl">
        <MaskLines lines={["民家"]} delay={1.1} />
      </motion.div>
      <motion.div style={{ y: titleY, rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative mx-auto w-full max-w-[1400px]">
        <MaskLines className="font-mono-s text-xs uppercase tracking-[0.35em] text-[#A8DADC]" lines={["A feature screenplay · Supernatural Thriller / Horror"]} />
        <h1 data-testid="hero-title-minka" className="font-brush text-[21vw] font-normal leading-[0.95] tracking-[0.03em] text-[#C81D25] ink-damage md:text-[19vw]" style={{ transform: "translateZ(60px)", textShadow: "0 10px 60px rgba(0,0,0,0.8)" }}>
          <Letters />
        </h1>
        <div className="mt-6 grid gap-8 md:mt-10 md:grid-cols-[1fr_auto] md:items-end">
          <motion.p
            data-testid="hero-hell-subline"
            className="font-hand text-sm uppercase leading-loose tracking-[0.2em] text-[#E2E0D8] drop-shadow-[0_2px_18px_rgba(200,29,37,0.35)] sm:text-base md:text-xl"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 1, 0] }}
            transition={{ duration: 7, times: [0, 0.3, 0.7, 1], repeat: Infinity, repeatDelay: 1.5, delay: 1.5, ease: "easeInOut" }}
          >
            Be careful what you wish for . . .<br />it might put you through hell
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.6, duration: 0.9 }} className="flex flex-col gap-4 md:items-end">
            <p className="font-mono-s text-xs uppercase tracking-[0.25em] text-[#A3ACBB]">Written by <span className="text-[#E2E0D8]">Nicola Davis</span></p>
            <div className="flex flex-wrap gap-3">
              <a href="#synopsis" data-testid="hero-read-synopsis-btn" className="group flex items-center gap-2 rounded-full bg-[#E2E0D8] px-6 py-3 font-mono-s text-xs uppercase tracking-[0.2em] text-[#07080B] transition-colors duration-300 hover:bg-[#C81D25] hover:text-[#E2E0D8]">
                Read the synopsis <ArrowDown size={14} className="transition-transform duration-300 group-hover:translate-y-1" />
              </a>
              <Link to="/film-options" data-testid="hero-options-btn" className="rounded-full border border-white/25 px-6 py-3 font-mono-s text-xs uppercase tracking-[0.2em] text-[#E2E0D8] transition-colors duration-300 hover:border-[#C81D25] hover:text-[#E76F51]">
                Film options
              </Link>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};
